import { useEffect, useState } from 'react';

import {
  VStack,
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  DrawerCloseButton,
  useDisclosure,
  Button,
  Heading,
} from '@chakra-ui/react';

import {
  useCreateTaskMutation,
  useGetTaskByIdQuery,
  useGetWorkflowByIdQuery,
  useRejectTaskMutation,
  useSubmitTaskMutation,
} from '../../../services';
import { CenteredSpinner, DeleteModal } from '../../../components';
import { useTypedSelector } from '../../../hooks';
import { FeaturesTabs, FormFelidWithAnswer } from '.';
import { FileStatus, NewTask, TaskStatus, Workflow } from '../../../models';
import { NewTaskFeaturesTabs } from './NewTaskFeatures';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

/**
 * Named on purpose (it used to be an inline object literal). The tasks page
 * passed `parentTaskId` while this component declared `linkedTaskId`, so the
 * value was dropped without a word and every sub-task was created unlinked.
 * With the props named here, the page and this component are held to one
 * contract and a rename on either side stops compiling instead of silently
 * losing data.
 */
export interface SavedTaskProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: number;
  taskId?: number;
  setNewTaskId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setNewTaskFiles: React.Dispatch<
    React.SetStateAction<
      {
        file: File;
        status: FileStatus;
      }[]
    >
  >;
  openTask: (taskId: number) => void;
  newTaskFiles: { file: File; status: FileStatus }[];
  startNewLinkedTask: (parentTaskId: number) => void;
  /**
   * The task this drawer is creating a SUB-TASK of, when the user got here
   * through "Create Sub Form". Undefined for an ordinary top-level task.
   *
   * The name is the backend's: `TaskRequest` declares `parentTaskId`, so prop,
   * model (`NewTask`) and wire field all read the same word end to end.
   */
  parentTaskId?: number;
}

export const SavedTask = ({
  isOpen,
  onClose,
  workflowId,
  taskId,
  newTaskFiles,
  setNewTaskFiles,
  setNewTaskId,
  startNewLinkedTask,
  parentTaskId,
  openTask,
}: SavedTaskProps) => {
  const [workflow, setWorkflow] = useState<Workflow>();
  const [formAnswers, setFormAnswers] = useState<
    {
      fieldId: number;
      answer: string;
    }[]
  >([]);
  const { userDetails } = useTypedSelector(state => state.auth);

  const { data: taskData, isLoading: isLoadingTaskData } = useGetTaskByIdQuery(
    Number(taskId),
    {
      skip: !taskId,
    }
  );

  const { data: workflowData, isLoading } = useGetWorkflowByIdQuery(
    workflowId,
    {
      skip: !workflowId,
    }
  );

  useEffect(() => {
    if (workflowData?.data || taskData?.data) {
      setNewTaskId(undefined);
      setWorkflow(workflowData?.data || taskData?.data.workflow);
    }
  }, [workflowData?.data, taskData?.data]);

  const [createTask, { isLoading: isCreateTaskLoading }] =
    useCreateTaskMutation();
  const [submitTask, { isLoading: isSubmitTaskLoading }] =
    useSubmitTaskMutation();
  const [rejectTask, { isLoading: isRejectTaskLoading }] =
    useRejectTaskMutation();

  const {
    isOpen: isRejectTaskModalOpen,
    onOpen: onOpenRejectTaskModal,
    onClose: onCloseRejectTaskModal,
  } = useDisclosure();

  const handleChangeAnswer = (fieldId: number, answer: string) => {
    const field = formAnswers.find(
      formAnswer => formAnswer.fieldId === fieldId
    );

    if (field)
      setFormAnswers(
        formAnswers.map(formAnswer => {
          if (formAnswer.fieldId === fieldId) {
            return { ...formAnswer, answer };
          } else {
            return formAnswer;
          }
        })
      );
    else setFormAnswers([...formAnswers, { fieldId, answer }]);
  };

  const handleSubmitTask = async () => {
    if (taskId) {
      const response = await submitTask({ taskId: Number(taskId), formAnswers });

      // The drawer used to close on any reply, throwing away every answer the
      // user had just typed even when the submit was rejected.
      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: 'Could not send this task',
          description:
            'Your answers were not submitted — they are still here. Please try again.',
        });
        return;
      }

      onClose();
    } else {
      // `.unwrap()` only rejects on the transport shape and destructured the
      // body blind: a body-level failure closed the drawer with no task
      // created, and a transport failure threw out of this click handler.
      //
      // The parent link is sent as `parentTaskId` because that is the field
      // `TaskRequest` reads; the previous `linkedTaskId` was read by nothing,
      // so the server stored an orphan sub-task. `!== undefined` rather than a
      // truthiness test so a parent id of 0 would still count as a parent, and
      // the key is OMITTED entirely for a top-level task rather than sent as
      // undefined — the DTO's `long parentTaskId` would read a present-but-null
      // field as 0 either way, but an absent key states the intent.
      const isChildTask = parentTaskId !== undefined;

      const newTask: NewTask = {
        workflowId,
        formAnswers,
        isChildTask,
        ...(isChildTask ? { parentTaskId } : {}),
      };

      const response = await createTask(newTask);

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: 'Could not create this task',
          description:
            'The task was not created — your answers are still here. Please try again.',
        });
        return;
      }

      const { data } = response as { data: { data?: { id?: number } } };

      if (data.data && data.data.id) {
        setNewTaskId(data.data.id);
      }
      onClose();
    }
  };

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />

      {isRejectTaskModalOpen && (
        <DeleteModal
          isOpen={isRejectTaskModalOpen}
          onClose={async isRejected => {
            if (!isRejected) return onCloseRejectTaskModal();

            const response = await rejectTask({ taskId: Number(taskId) });

            // Keep the confirmation open on failure: dismissing it made a
            // rejected reject look done.
            if (!isMutationSuccess(response)) {
              notifyMutationFailure(response, {
                title: 'Could not reject this task',
                description: 'The task was not rejected. Please try again.',
              });
              return;
            }

            onCloseRejectTaskModal();
          }}
        />
      )}

      <DrawerContent pb="5" bg="custom_gray.2" height={'92.5vh'}>
        <DrawerCloseButton color="white" top="-12" size="lg" />

        <DrawerHeader bg="custom_gray.2">
          <HStack justify="space-between">
            <Heading as="h2" fontWeight="normal" fontSize="4xl">
              {workflow?.name}
            </Heading>

            <HStack justifyContent={'center'} alignItems={'center'}>
              {!taskData?.data.taskHierarchy?.find(child => child.level > 1) &&
                ((taskData?.data?.status as TaskStatus) ===
                  TaskStatus.Completed ||
                  (taskData?.data?.status as TaskStatus) ===
                    TaskStatus.Cancelled) && (
                  <Button
                    sx={{ ml: 5 }}
                    bg="transparent"
                    _hover={{ bg: 'custom_orange.2', color: 'white' }}
                    color="custom_orange.2"
                    border="1px"
                    borderColor="custom_orange.2"
                    type="submit"
                    px="6"
                    onClick={() =>
                      startNewLinkedTask(taskData?.data?.id as number)
                    }
                    // isLoading={}
                  >
                    Create Sub Form
                  </Button>
                )}
              {(!taskData ||
                ([TaskStatus.InProgress, TaskStatus.Late].includes(
                  taskData?.data.status as TaskStatus
                ) &&
                  taskData?.data.currentAssignees.find(
                    assignee => assignee.id === userDetails?.id
                  ))) && (
                <HStack>
                  {taskId && taskData?.data.currentNode.hasRejection && (
                    <Button
                      bg="transparent"
                      _hover={{ bg: 'transparent' }}
                      color="custom_orange.2"
                      fontWeight="semibold"
                      fontSize="sm"
                      variant="outline"
                      borderColor="custom_orange.2"
                      onClick={onOpenRejectTaskModal}
                      isLoading={isRejectTaskLoading}
                    >
                      Reject
                    </Button>
                  )}

                  <Button
                    bg="custom_orange.2"
                    _hover={{ bg: 'custom_orange.2' }}
                    color="white"
                    type="submit"
                    px="6"
                    onClick={handleSubmitTask}
                    isLoading={isCreateTaskLoading || isSubmitTaskLoading}
                  >
                    {taskId ? 'Send' : 'Submit'}
                  </Button>
                </HStack>
              )}
            </HStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody bg="custom_gray.2">
          <Box>
            {!workflow || isLoading ? (
              <CenteredSpinner />
            ) : (
              <HStack h="80vh" justify="space-between" gap="2%">
                <VStack
                  bg="white"
                  minH="80vh"
                  h="full"
                  overflowY="auto"
                  border="1px"
                  borderColor="custom_gray.10"
                  borderRadius="md"
                  flexBasis="68%"
                  flexGrow="1"
                  align="start"
                  px="12"
                  py="4"
                  gap="5"
                >
                  {[...workflow.form.fields]
                    .sort((a, b) => a.orderNumber - b.orderNumber)
                    .filter(({ whoCanView }) => {
                      const isUserCanView =
                        whoCanView[0] === 'all' ||
                        whoCanView?.includes(String(userDetails?.role.id));

                      return isUserCanView || !taskId;
                    })
                    .map(formField => {
                      return (
                        <VStack key={formField.id} align="start" w="full">
                          <FormFelidWithAnswer
                            onChangeAnswer={handleChangeAnswer}
                            formField={formField}
                            taskData={taskData?.data}
                          />
                        </VStack>
                      );
                    })}
                </VStack>
                {taskData && (
                  <FeaturesTabs
                    taskData={taskData.data}
                    files={newTaskFiles}
                    setFiles={setNewTaskFiles}
                    openTask={openTask}
                  />
                )}
                {!taskData && (
                  <NewTaskFeaturesTabs
                    workflowData={workflow}
                    files={newTaskFiles}
                    setFiles={setNewTaskFiles}
                  />
                )}
              </HStack>
            )}
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
